// useTemplates.ts
import { useCallback, useState } from "react"
import { useMutation } from "@tanstack/react-query"
import { useAuthedAxios } from "hooks/core/useAuthedAxios"
import {
  listTemplates,
  mergeTemplate,
  parseTemplate,
  listAllTemplateFiles,
} from "api/Engine/templates"
import {
  TemplateFileListApiResponse,
  TemplateListApiResponse,
  TemplateParseApiResponse,
  TemplateMergeApiResponse,
  SelectedFile,
  TemplateOptions,
  UserInputs,
} from "types/template.types"

/**
 * Template operations for the questionnaire template wizard. All imperative
 * (one-shot calls inside the wizard flow): reads are plain authed calls with
 * a loading flag, parse/merge run as react-query mutations.
 */
interface UseTemplatesReturn {
  listTemplateFiles: () => Promise<TemplateFileListApiResponse>
  listingTemplateFiles: boolean

  listTemplatesFromFile: (
    selectedFile: SelectedFile
  ) => Promise<TemplateListApiResponse>
  listingTemplatesFromFile: boolean

  parseSelectedTemplate: (
    id: string,
    selectedFile: SelectedFile,
    template: string,
    options: TemplateOptions
  ) => Promise<TemplateParseApiResponse>
  parsingSelectedTemplate: boolean

  mergeTemplateWithUserInputs: (
    userInputs: UserInputs,
    selectedFile: SelectedFile,
    options: TemplateOptions
  ) => Promise<TemplateMergeApiResponse>
  mergingTemplateWithUserInputs: boolean
}

export const useTemplates = (): UseTemplatesReturn => {
  const getAxios = useAuthedAxios()

  const [listingTemplateFiles, setListingTemplateFiles] = useState(false)
  const [listingTemplatesFromFile, setListingTemplatesFromFile] =
    useState(false)

  const listTemplateFiles =
    useCallback(async (): Promise<TemplateFileListApiResponse> => {
      setListingTemplateFiles(true)
      try {
        return await listAllTemplateFiles(await getAxios())
      } finally {
        setListingTemplateFiles(false)
      }
    }, [getAxios])

  const listTemplatesFromFile = useCallback(
    async (selectedFile: SelectedFile): Promise<TemplateListApiResponse> => {
      setListingTemplatesFromFile(true)
      try {
        return await listTemplates(await getAxios(), selectedFile)
      } finally {
        setListingTemplatesFromFile(false)
      }
    },
    [getAxios]
  )

  const parseMutation = useMutation({
    mutationFn: async (
      args: [string, SelectedFile, string, TemplateOptions]
    ) => parseTemplate(await getAxios(), ...args),
  })

  const mergeMutation = useMutation({
    mutationFn: async (args: [UserInputs, SelectedFile, TemplateOptions]) =>
      mergeTemplate(await getAxios(), ...args),
  })

  const parseSelectedTemplate = useCallback(
    (
      id: string,
      selectedFile: SelectedFile,
      template: string,
      options: TemplateOptions
    ) => parseMutation.mutateAsync([id, selectedFile, template, options]),
    [parseMutation.mutateAsync]
  )

  const mergeTemplateWithUserInputs = useCallback(
    (
      userInputs: UserInputs,
      selectedFile: SelectedFile,
      options: TemplateOptions
    ) => mergeMutation.mutateAsync([userInputs, selectedFile, options]),
    [mergeMutation.mutateAsync]
  )

  return {
    listTemplateFiles,
    listingTemplateFiles,
    listTemplatesFromFile,
    listingTemplatesFromFile,
    parseSelectedTemplate,
    parsingSelectedTemplate: parseMutation.isPending,
    mergeTemplateWithUserInputs,
    mergingTemplateWithUserInputs: mergeMutation.isPending,
  }
}
